import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, LayoutDashboard } from "lucide-react";
import Logo from "../dashboard/Logo";
import { useAuth } from "../../contexts/AuthContext";
import { useProfile } from "../../contexts/ProfileContext";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

type NavbarProps = {
  onSignIn?: () => void;
  onRegister?: () => void;
};

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/pricing" },
  { label: "Bootcamp", to: "/bootcamp" },
  { label: "Contact Us", to: "/contact" },
] as const;

export function Navbar(props?: NavbarProps): JSX.Element {
  const { onSignIn, onRegister } = props ?? {};
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { isAuthenticated, signOut } = useAuth();
  const { profile } = useProfile();

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 150); // Small delay to allow moving mouse from trigger to content
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const navigate = useNavigate();

  const handleSignIn = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault(); // Prevent default Link navigation
    if (typeof onSignIn === "function") {
      onSignIn();
    } else {
      navigate("/auth?mode=login");
    }
  };

  const handleRegister = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault(); // Prevent default Link navigation
    if (typeof onRegister === "function") {
      onRegister();
    } else {
      navigate("/auth?mode=register");
    }
  };

  const navigationSchema = {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    name: NAV_LINKS.map((link) => link.label),
    url: NAV_LINKS.map((link) => `https://bandarmolony.com${link.to}`),
  } as const;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full bg-background/95 backdrop-blur-md shadow-lg">
      <nav
        role="navigation"
        aria-label="Main navigation"
        className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-4 md:px-8"
      >
        <Link
          to="/"
          onClick={closeMenu}
          className="flex items-center gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          <Logo
            showText
            className="items-center gap-0"
            badgeClassName="h-8 w-10 md:h-9 md:w-11 transform -translate-y-[2px]"
            textClassName="text-2xl font-semibold tracking-tight text-foreground dark:text-white"
          />
        </Link>

        <div className="hidden items-center gap-10 md:flex">
          <div className="flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-medium text-foreground transition-colors hover:text-primary opacity-80 hover:opacity-100 dark:text-white/80 dark:hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              !profile ? (
                <div className="flex items-center gap-2 px-3 py-1.5 animate-pulse">
                  <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700/50"></div>
                  <div className="hidden sm:block space-y-1.5">
                    <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700/50 rounded"></div>
                    <div className="h-2 w-12 bg-slate-200 dark:bg-slate-700/50 rounded"></div>
                  </div>
                </div>
              ) : (
              <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen} modal={false}>
                <DropdownMenuTrigger asChild>
                  <button 
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    className="flex items-center gap-2 hover:bg-accent hover:text-accent-foreground active:bg-accent/80 rounded-lg px-3 py-1.5 transition-all duration-200 group relative outline-none"
                  >
                    <Avatar className="h-8 w-8 ring-2 ring-transparent group-hover:ring-blue-200 dark:group-hover:ring-blue-800 transition-all duration-200">
                      <AvatarImage src={profile?.avatarUrl || profile?.avatar} alt="User Avatar" />
                      <AvatarFallback className="bg-muted text-muted-foreground group-hover:bg-blue-100 dark:group-hover:bg-blue-900 transition-colors duration-200">
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium leading-none group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                        {profile?.full_name || profile?.name || 'User'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 group-hover:text-blue-500 dark:group-hover:text-blue-300 transition-colors duration-200">
                        {profile?.subscriptionStatus === 'trial'
                          ? 'Free Trial'
                          : (profile?.subscriptionPlan || 'Free') + ' Plan'}
                      </div>
                    </div>
                    {/* Hover indicator */}
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-blue-500 group-hover:w-8 transition-all duration-200"></div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-56 mt-2"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    <span>Back to dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { signOut(); navigate('/'); }} className="cursor-pointer text-red-500 focus:text-red-500">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              )
            ) : (
              <>
                <a
                  href="#"
                  onClick={handleSignIn}
                  className="rounded-full border border-foreground/40 px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-foreground/10 dark:border-white/60 dark:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary cursor-pointer"
                >
                  Sign In
                </a>
                <a
                  href="#"
                  onClick={handleRegister}
                  className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary cursor-pointer"
                >
                  Register
                </a>
              </>
            )}
          </div>
        </div>

        <button
          type="button"
          className="flex items-center justify-center rounded-full border border-foreground/30 p-2 text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:border-white/40 dark:text-white md:hidden"
          onClick={toggleMenu}
          aria-controls="primary-navigation"
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? (
            <X className="h-5 w-5" aria-hidden="true" />
          ) : (
            <Menu className="h-5 w-5" aria-hidden="true" />
          )}
          <span className="sr-only">Toggle navigation</span>
        </button>
      </nav>

      <div
        id="primary-navigation"
        className={`md:hidden transition-[max-height] duration-300 ease-in-out ${
          isMenuOpen ? "max-h-96" : "max-h-0"
        } overflow-hidden border-t border-border bg-background/95 backdrop-blur dark:border-white/10`}
      >
        <div className="space-y-3 px-6 py-6">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={closeMenu}
              className="block rounded-lg px-4 py-3 text-base font-medium text-foreground transition-colors hover:bg-primary/10 hover:text-primary opacity-80 hover:opacity-100 dark:text-white/80 dark:hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col gap-3 pt-2">
            {isAuthenticated ? (
              !profile ? (
                <>
                  <div className="flex items-center gap-3 px-4 py-2 border-b border-border mb-2 animate-pulse">
                    <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700/50"></div>
                    <div className="space-y-1.5">
                      <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700/50 rounded"></div>
                      <div className="h-2 w-16 bg-slate-200 dark:bg-slate-700/50 rounded"></div>
                    </div>
                  </div>
                  <div className="px-4 py-3 animate-pulse">
                    <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700/50 rounded"></div>
                  </div>
                  <div className="px-4 py-3 animate-pulse">
                    <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700/50 rounded"></div>
                  </div>
                </>
              ) : (
              <>
                <div className="flex items-center gap-3 px-4 py-2 border-b border-border mb-2">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={profile?.avatarUrl || profile?.avatar} alt="User Avatar" />
                    <AvatarFallback className="bg-muted text-muted-foreground">
                      <User className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <div className="text-sm font-medium text-foreground">
                      {profile?.full_name || profile?.name || 'User'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {profile?.subscriptionStatus === 'trial'
                        ? 'Free Trial'
                        : (profile?.subscriptionPlan || 'Free') + ' Plan'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => { navigate('/dashboard'); closeMenu(); }}
                  className="flex items-center rounded-lg px-4 py-3 text-base font-medium text-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                >
                  <LayoutDashboard className="mr-3 h-5 w-5" />
                  Back to dashboard
                </button>
                <button
                  onClick={() => { signOut(); closeMenu(); navigate('/'); }}
                  className="flex items-center rounded-lg px-4 py-3 text-base font-medium text-red-500 transition-colors hover:bg-red-500/10"
                >
                  <LogOut className="mr-3 h-5 w-5" />
                  Log out
                </button>
              </>
              )
            ) : (
              <>
                <a
                  href="#"
                  onClick={(e) => {
                    handleSignIn(e);
                    closeMenu();
                  }}
                  className="rounded-full border border-foreground/40 px-4 py-3 text-center text-sm font-semibold text-foreground transition-colors hover:bg-foreground/10 dark:border-white/60 dark:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary cursor-pointer"
                >
                  Sign In
                </a>
                <a
                  href="#"
                  onClick={(e) => {
                    handleRegister(e);
                    closeMenu();
                  }}
                  className="rounded-full bg-primary px-4 py-3 text-center text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary cursor-pointer"
                >
                  Register
                </a>
              </>
            )}
          </div>
        </div>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(navigationSchema) }}
      />
    </header>
  );
}
